import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CMSUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
  status: string;
}

interface CMSAuthContextType {
  user: CMSUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
}

const CMSAuthContext = createContext<CMSAuthContextType | undefined>(undefined);

export const useCMSAuth = () => {
  const context = useContext(CMSAuthContext);
  if (context === undefined) {
    throw new Error('useCMSAuth must be used within a CMSAuthProvider');
  }
  return context;
};

export const CMSAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CMSUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hoisted above the useEffect so react-hooks/immutability passes.
  const checkAuth = React.useCallback(async () => {
    try {
      const sessionToken = localStorage.getItem('cms_session_token');
      if (!sessionToken) {
        setIsLoading(false);
        return;
      }

      // Validate session via edge function (service_role, no anon DB access)
      const { data, error } = await supabase.functions.invoke('cms-validate-session', {
        body: { sessionToken }
      });

      if (error || !data?.valid || !data?.user) {
        localStorage.removeItem('cms_session_token');
        setUser(null);
        setIsLoading(false);
        return;
      }

      setUser(data.user as CMSUser);
    } catch {
      console.error('Auth check failed');
      localStorage.removeItem('cms_session_token');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Mount-time async fetch (validates server session). Canonical
  // useEffect → fetch → setState pattern; the rule misfires here.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('cms-auth', {
        body: { action: 'login', email, password }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.success) {
        localStorage.setItem('cms_session_token', data.sessionToken);
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      const sessionToken = localStorage.getItem('cms_session_token');
      if (sessionToken) {
        await supabase.functions.invoke('cms-auth', {
          body: { action: 'logout', sessionToken }
        });
      }
    } catch (error) {
      console.error('Logout error');
    } finally {
      localStorage.removeItem('cms_session_token');
      setUser(null);
    }
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.roles.includes(role);
  };

  const value = { user, isLoading, login, logout, hasRole };

  return <CMSAuthContext.Provider value={value}>{children}</CMSAuthContext.Provider>;
};
